import { useCallback, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { useGmailStatus } from '@/hooks/integrations/google/useGmailStatus';
import { useGoogleOAuthConnect } from '@/hooks/useGoogleOAuthConnect';
import AuthLayout from '@/layouts/AuthLayout';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { apiPost } from '@/lib/http/client';
import { getUnavailableCapabilities } from '@/lib/integrations/scopeCapabilities';

const CAPABILITY_CONSEQUENCE: Record<string, string> = {
  Gmail: "Email import won't run",
  Calendar: "Calendar import won't run",
  Meet: "Meeting recordings won't be captured",
  'Gmail drafts': "Prepared emails can't be placed in your Gmail drafts",
};

// Fallback for required scopes that don't map to a named capability
// (e.g. drive.file), so the screen and skip CTA stay informative.
const GENERIC_CONSEQUENCE = "Some Google features won't work";

export default function ScopePermissionsRequired() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useGmailStatus(orgId, { enabled: !!orgId });
  const allMissing = useMemo(
    () =>
      (data?.accounts ?? []).flatMap(account => account.missing_scopes ?? []),
    [data]
  );
  const capabilities = useMemo(
    () => [...getUnavailableCapabilities(allMissing)],
    [allMissing]
  );
  const { connect, isConnecting } = useGoogleOAuthConnect(orgId);

  const handleGrant = useCallback(() => {
    connect();
  }, [connect]);

  const handleSkip = useCallback(async () => {
    try {
      await apiPost('/users/api/scope-skip/', { org_id: orgId });
    } catch {
      // Skipping stays non-blocking even if the log call fails.
    }
    navigate(`/${orgId}`);
  }, [orgId, navigate]);

  if (isLoading) {
    return (
      <AuthLayout title='Grant Google permissions'>
        <div className='flex flex-col items-center gap-4 py-12'>
          <LoadingIndicator size='md' />
        </div>
      </AuthLayout>
    );
  }

  if (allMissing.length === 0) {
    return <Navigate to={`/${orgId}`} replace />;
  }

  const mappedConsequences = capabilities
    .map(capability => CAPABILITY_CONSEQUENCE[capability] ?? capability)
    .filter(Boolean);
  const consequences =
    mappedConsequences.length > 0 ? mappedConsequences : [GENERIC_CONSEQUENCE];

  return (
    <AuthLayout title='Grant Google permissions'>
      <div className='flex flex-col gap-8'>
        <div className='flex flex-col gap-3'>
          <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
            Grant Google permissions
          </h1>
          <p className='text-base text-gray-600 dark:text-gray-400'>
            Without these permissions, some Ahoy features won't work:
          </p>
        </div>
        <ul className='flex flex-col gap-2 list-disc pl-5 text-sm text-gray-600 dark:text-gray-400'>
          {consequences.map(consequence => (
            <li key={consequence}>{consequence}</li>
          ))}
        </ul>
        <div className='flex flex-col gap-3'>
          <Button
            className='w-full'
            isDisabled={isConnecting}
            onClick={handleGrant}
          >
            {isConnecting ? 'Connecting…' : 'Grant permissions'}
          </Button>
          <div className='flex flex-col gap-2'>
            <Button className='w-full' color='secondary' onClick={handleSkip}>
              Skip for now
            </Button>
            <p className='text-center text-sm text-gray-600 dark:text-gray-400'>
              You can grant these later, but the features above won't work until
              you do.
            </p>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
